import { KeywordResearchProject, BusinessProfile } from '../types';
import { cloudSyncService } from './cloudSyncService';

const KEYWORD_PROJECTS_KEY = 'zynex_ninja_keyword_projects_v1';
const ACTIVE_KEYWORD_PROJECT_KEY = 'zynex_ninja_active_keyword_project_id';

const defaultBusinessProfile: BusinessProfile = {
  businessName: '',
  websiteUrl: '',
  primaryLocation: '',
  targetCities: [],
  regionState: '',
  country: 'USA',
  businessType: 'service',
  mainServices: [],
  secondaryServices: [],
  serviceAreas: [],
  targetCustomers: '',
  commercialGoals: '',
  confirmedServices: [],
  unverifiedServices: [],
  unsupportedServices: [],
};

export class KeywordResearchStorageService {
  static getAllProjects(): KeywordResearchProject[] {
    try {
      const raw = localStorage.getItem(KEYWORD_PROJECTS_KEY);
      let list: KeywordResearchProject[] = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(list)) list = [];

      // Purge any accidental Apex project
      list = list.filter(p => !p.clientName?.toLowerCase().includes('apex') && !p.id.toLowerCase().includes('apex'));

      let needsSave = false;
      list = list.map(p => {
        if (p.clientName?.includes('Door') || p.websiteUrl?.includes('reflectwindow.com')) {
          needsSave = true;
          return this.createReflectWindowProject();
        }
        return p;
      });

      if (list.length === 0) {
        const ut = this.createUnitedTruckingProject();
        const rw = this.createReflectWindowProject();
        list = [ut, rw];
        needsSave = true;
      }

      if (needsSave) {
        localStorage.setItem(KEYWORD_PROJECTS_KEY, JSON.stringify(list));
      }

      return list;
    } catch {
      const ut = this.createUnitedTruckingProject();
      const rw = this.createReflectWindowProject();
      const list = [ut, rw];
      localStorage.setItem(KEYWORD_PROJECTS_KEY, JSON.stringify(list));
      return list;
    }
  }

  static createUnitedTruckingProject(): KeywordResearchProject {
    return {
      id: 'kw-project-united-trucking',
      clientName: 'United Trucking Supplies',
      websiteUrl: 'https://unitedtruckingsupplies.com',
      linkedAuditId: 'audit_united_trucking',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      currentPhase: 2,
      businessProfile: {
        ...defaultBusinessProfile,
        businessName: 'United Trucking Supplies',
        websiteUrl: 'https://unitedtruckingsupplies.com',
        primaryLocation: 'Trinidad and Tobago',
        targetCities: ['Port of Spain', 'San Fernando', 'Chaguanas', 'Arima'],
        regionState: 'Trinidad',
        country: 'Trinidad and Tobago',
        businessType: 'hybrid',
        mainServices: ['Truck Parts Supply', 'Heavy Duty Truck Parts', 'Auto Parts Supply', 'Bus Parts Supply'],
        secondaryServices: ['Commercial Fleet Spare Parts', 'Brake & Suspension Replacement Parts'],
        serviceAreas: ['Trinidad and Tobago', 'Port of Spain', 'San Fernando'],
        targetCustomers: 'Commercial truck drivers, fleet operators, mechanics, and automotive owners',
        commercialGoals: 'Rank #1 in Trinidad for heavy duty truck and auto parts, driving direct phone calls and store visits',
        confirmedServices: [
          'Heavy Duty Truck Parts Supply',
          'Truck Parts Supply',
          'Bus Parts Supply',
          'Auto Parts Supply',
          'Commercial Fleet Spare Parts',
          'Brake & Suspension Replacement Parts',
        ],
        unverifiedServices: [],
        unsupportedServices: [],
      },
      seedKeywords: [
        { id: 'sk-ut-1', seedKeyword: 'truck parts trinidad', location: 'Trinidad and Tobago', intent: 'Commercial', relevant: true, notes: 'Core service seed keyword' },
        { id: 'sk-ut-2', seedKeyword: 'auto parts trinidad', location: 'Trinidad and Tobago', intent: 'Commercial', relevant: true, notes: 'Automotive spares seed' },
        { id: 'sk-ut-3', seedKeyword: 'bus parts trinidad', location: 'Trinidad and Tobago', intent: 'Commercial', relevant: true, notes: 'Bus fleet spare parts seed' },
        { id: 'sk-ut-4', seedKeyword: 'heavy duty truck parts trinidad', location: 'Trinidad and Tobago', intent: 'Commercial', relevant: true, notes: 'Commercial heavy machinery seed' },
      ],
      variations: [
        { id: 'var-ut-1', keyword: 'trinidad auto parts', variationOf: 'auto parts trinidad', searchVolumeStatus: 'collected', searchVolume: 2400, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Assigned Variation (Primary Target - Step 10)', step10Assigned: true, targetTier: 'Primary', verificationStatus: 'user_verified' },
        { id: 'var-ut-2', keyword: 'Auto Parts in Trinidad and Tobago', variationOf: 'auto parts trinidad', searchVolumeStatus: 'collected', searchVolume: 1100, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Assigned Variation (Primary Target - Step 10)', step10Assigned: true, targetTier: 'Primary', verificationStatus: 'user_verified' },
        { id: 'var-ut-3', keyword: 'auto parts place in trinidad', variationOf: 'auto parts trinidad', searchVolumeStatus: 'collected', searchVolume: 500, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Assigned Variation (Primary Target - Step 10)', step10Assigned: true, targetTier: 'Primary', verificationStatus: 'user_verified' },
        { id: 'var-ut-4', keyword: 'truck parts trinidad', variationOf: 'truck parts in trinidad', searchVolumeStatus: 'collected', searchVolume: 1900, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Assigned Variation (Secondary Target - Step 10)', step10Assigned: true, targetTier: 'Secondary', verificationStatus: 'user_verified' },
        { id: 'var-ut-5', keyword: 'truck parts for sale in trinidad', variationOf: 'truck parts in trinidad', searchVolumeStatus: 'collected', searchVolume: 950, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Assigned Variation (Secondary Target - Step 10)', step10Assigned: true, targetTier: 'Secondary', verificationStatus: 'user_verified' },
        { id: 'var-ut-6', keyword: 'Truck Parts Trinidad and Tobago', variationOf: 'truck parts in trinidad', searchVolumeStatus: 'collected', searchVolume: 650, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Assigned Variation (Secondary Target - Step 10)', step10Assigned: true, targetTier: 'Secondary', verificationStatus: 'user_verified' },
        { id: 'var-ut-7', keyword: 'heavy duty truck parts in trinidad', variationOf: 'heavy duty truck parts trinidad', searchVolumeStatus: 'collected', searchVolume: 880, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Commercial fleet variation', verificationStatus: 'user_verified' },
      ],
      websiteContentKeywords: [
        { id: 'wk-ut-1', keyword: 'Truck Parts Trinidad & Auto Parts Trinidad', sourcePage: 'Homepage', whereFound: 'Title', intent: 'Commercial', relevant: true, existingTarget: true, verificationStatus: 'user_verified' },
        { id: 'wk-ut-2', keyword: 'Truck and auto parts supplier Trinidad', sourcePage: 'Homepage', whereFound: 'Meta', intent: 'Commercial', relevant: true, existingTarget: true, verificationStatus: 'user_verified' },
      ],
      competitorKeywords: [],
      toolKeywords: [],
      autocompleteKeywords: [],
      aeoQuestions: [],
      masterKeywords: [
        { id: 'mk-ut-1', keyword: 'auto parts trinidad', searchVolume: 2400, intent: 'Commercial', priority: 'Primary', type: 'Target', source: 'Core Seed', targetPage: 'Homepage / Primary Target Page', action: 'Primary Target', status: 'active' },
        { id: 'mk-ut-2', keyword: 'truck parts in trinidad', searchVolume: 1900, intent: 'Commercial', priority: 'Secondary', type: 'Target', source: 'Core Seed', targetPage: 'Core Service Page', action: 'Secondary Target', status: 'active' },
        { id: 'mk-ut-3', keyword: 'bus parts trinidad', searchVolume: 720, intent: 'Commercial', priority: 'Tertiary', type: 'Target', source: 'Core Seed', targetPage: 'Supporting Service Page', action: 'Tertiary Target', status: 'active' },
        { id: 'mk-ut-4', keyword: 'heavy duty truck parts trinidad', searchVolume: 880, intent: 'Commercial', priority: 'Primary', type: 'Target', source: 'Seed Expansion', targetPage: '/heavy-duty-truck-parts/', action: 'Commercial Fleet Landing Page', status: 'active' },
      ],
      pageMappings: [
        { id: 'pm-ut-1', keyword: 'auto parts trinidad', priority: 'Primary', intent: 'Commercial', targetPage: 'Homepage - United TruckingSupplies', existingOrNew: 'Existing Page', targetUrl: '/', reason: 'Core commercial revenue driver' },
        { id: 'pm-ut-2', keyword: 'truck parts in trinidad', priority: 'Secondary', intent: 'Commercial', targetPage: 'Homepage - United TruckingSupplies', existingOrNew: 'Existing Page', targetUrl: '/', reason: 'High search volume commercial category' },
      ],
      servicePageMappings: [
        {
          id: 'spm-1',
          pageName: 'Truck Parts Supply Trinidad',
          targetUrl: '/services/Truck-Parts-Supply-trinidad',
          primaryKeyword: 'genuine Truck Parts Supply Trinidad',
          primaryKeyword2: 'Truck Parts Supply Trinidad',
          secondaryKeyword: 'OEM Truck Parts Trinidad',
          secondaryKeyword2: 'Truck Parts Supply in Trinidad',
          tertiaryKeyword: 'Truck Parts Supply trinidad',
          tertiaryKeyword2: 'Truck Parts Supplier Trinidad',
          supportingKeywords: ['Genuine Truck Parts Supply', 'Truck Spare Parts Supplier Trinidad'],
          candidatePool: [
            { id: 'cp-1', keyword: 'Truck Parts Supply Trinidad', source: 'Modifier', searchVolume: 1900, intent: 'Commercial' },
            { id: 'cp-2', keyword: 'Truck Parts Supplier Trinidad', source: 'Modifier', searchVolume: 950, intent: 'Commercial' },
          ],
          methodsPerformed: { autocomplete: true, competitorScraping: true, relatedSearches: true, toolsImport: true, serpCheck: true }
        },
        {
          id: 'spm-2',
          pageName: 'Heavy duty truck parts Trinidad',
          targetUrl: '/services/Heavy-duty-truck-parts-trinidad',
          primaryKeyword: 'Genuine heavy duty truck parts supply trinidad and tobago',
          primaryKeyword2: 'heavy duty truck parts in trinidad',
          secondaryKeyword: 'OEM Heavy Duty Truck Parts Trinidad',
          secondaryKeyword2: 'heavy duty truck parts trinidad',
          tertiaryKeyword: 'commercial Heavy duty truck parts Trinidad',
          tertiaryKeyword2: 'Heavy Duty Truck Parts Supplier Trinidad',
          supportingKeywords: ['Commercial Heavy Duty Truck Spares', 'Heavy Machinery Parts Trinidad'],
          candidatePool: [
            { id: 'cp-3', keyword: 'heavy duty truck parts in trinidad', source: 'Modifier', searchVolume: 880, intent: 'Commercial' },
            { id: 'cp-4', keyword: 'Heavy Duty Truck Parts Supplier Trinidad', source: 'Modifier', searchVolume: 650, intent: 'Commercial' },
          ],
          methodsPerformed: { autocomplete: true, competitorScraping: true, relatedSearches: true, toolsImport: true, serpCheck: true }
        },
        {
          id: 'spm-3',
          pageName: 'Bus Parts Trinidad',
          targetUrl: '/services/Bus-Parts-Trinidad',
          primaryKeyword: 'Bus Parts Supplier Trinidad',
          primaryKeyword2: 'bus parts trinidad',
          secondaryKeyword: 'OEM Bus Parts Trinidad',
          secondaryKeyword2: 'Bus Fleet Spare Parts Trinidad',
          tertiaryKeyword: 'Genuine Bus Parts Trinidad',
          tertiaryKeyword2: 'Commercial Bus Parts Trinidad',
          supportingKeywords: ['Bus Parts Supplier Trinidad and Tobago', 'Bus Fleet Maintenance Parts'],
          candidatePool: [
            { id: 'cp-5', keyword: 'bus parts trinidad', source: 'Modifier', searchVolume: 720, intent: 'Commercial' },
            { id: 'cp-6', keyword: 'Bus Fleet Spare Parts Trinidad', source: 'Modifier', searchVolume: 450, intent: 'Commercial' },
          ],
          methodsPerformed: { autocomplete: true, competitorScraping: true, relatedSearches: true, toolsImport: true, serpCheck: true }
        }
      ],
      contentBriefs: [],
      decisionLog: [],
      homepageSeoTitle: 'Truck Parts Trinidad & Auto Parts Trinidad | United Trucking',
      homepageMetaDescription: 'Looking for truck parts in Trinidad? We supply genuine and aftermarket truck and auto parts across Trinidad. Contact United Trucking Supplies today!',
      actionPlanP1: [
        'Consolidate Truck Parts, Bus Parts, and Auto Parts silos with dedicated Trinidad navigation links',
        'Implement exact NAP consistency for United Trucking Supplies across Port of Spain and Trinidad directories',
      ],
      actionPlanP2: [],
      actionPlanP3: [],
      actionPlanP4: [],
    };
  }

  static createReflectWindowProject(): KeywordResearchProject {
    return {
      id: 'kw-project-reflect-window',
      clientName: 'Reflect Window Cleaning',
      websiteUrl: 'https://reflectwindowcleaning.com',
      linkedAuditId: 'audit_reflect_window',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      currentPhase: 2,
      businessProfile: {
        ...defaultBusinessProfile,
        businessName: 'Reflect Window Cleaning',
        websiteUrl: 'https://reflectwindowcleaning.com',
        primaryLocation: 'Edmonton, AB',
        targetCities: ['Edmonton', 'Sherwood Park', 'St. Albert', 'Spruce Grove'],
        regionState: 'Alberta',
        country: 'Canada',
        businessType: 'service',
        mainServices: ['Residential Window Cleaning', 'Commercial Window Cleaning', 'Gutter Cleaning', 'Pressure Washing', 'Solar Panel Cleaning'],
        secondaryServices: ['Screen Cleaning', 'Post-Construction Window Cleaning', 'Driveway Pressure Washing'],
        serviceAreas: ['Edmonton, AB', 'Sherwood Park, AB', 'St. Albert, AB'],
        targetCustomers: 'Homeowners, condo boards, retail property managers, and commercial building owners',
        commercialGoals: 'Rank #1 in Edmonton for residential & commercial window cleaning, gutter cleaning, and exterior pressure washing leads',
        confirmedServices: [
          'Residential Window Cleaning',
          'Commercial Window Cleaning',
          'Gutter Cleaning',
          'Pressure Washing',
          'Solar Panel Cleaning',
          'Screen Cleaning',
        ],
        unverifiedServices: [],
        unsupportedServices: [],
      },
      seedKeywords: [
        { id: 'sk-rw-1', seedKeyword: 'window cleaning edmonton', location: 'Edmonton, AB', intent: 'Commercial', relevant: true, notes: 'Primary high-intent service keyword' },
        { id: 'sk-rw-2', seedKeyword: 'residential window cleaning edmonton', location: 'Edmonton, AB', intent: 'Commercial', relevant: true, notes: 'Core residential client target' },
        { id: 'sk-rw-3', seedKeyword: 'gutter cleaning edmonton', location: 'Edmonton, AB', intent: 'Commercial', relevant: true, notes: 'High volume seasonal service' },
        { id: 'sk-rw-4', seedKeyword: 'pressure washing edmonton', location: 'Edmonton, AB', intent: 'Commercial', relevant: true, notes: 'Exterior washing service seed' },
      ],
      variations: [
        { id: 'var-rw-1', keyword: 'window washers edmonton', variationOf: 'window cleaning edmonton', searchVolumeStatus: 'collected', searchVolume: 2400, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Core service variation', verificationStatus: 'user_verified' },
        { id: 'var-rw-2', keyword: 'best window cleaning edmonton', variationOf: 'window cleaning edmonton', searchVolumeStatus: 'collected', searchVolume: 880, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'High intent local query', verificationStatus: 'user_verified' },
        { id: 'var-rw-3', keyword: 'exterior window cleaning edmonton', variationOf: 'window cleaning edmonton', searchVolumeStatus: 'collected', searchVolume: 720, intent: 'Commercial', serpSimilarity: 'High', separateTarget: false, reason: 'Residential variation', verificationStatus: 'user_verified' },
      ],
      websiteContentKeywords: [
        { id: 'wk-rw-1', keyword: 'Professional Window Cleaning & Gutter Cleaning Edmonton', sourcePage: 'Homepage', whereFound: 'Title', intent: 'Commercial', relevant: true, existingTarget: true, verificationStatus: 'user_verified' },
        { id: 'wk-rw-2', keyword: 'Reflect Window Cleaning Edmonton residential and commercial services', sourcePage: 'Homepage', whereFound: 'Meta', intent: 'Commercial', relevant: true, existingTarget: true, verificationStatus: 'user_verified' },
      ],
      competitorKeywords: [],
      toolKeywords: [],
      autocompleteKeywords: [],
      aeoQuestions: [],
      masterKeywords: [
        { id: 'mk-rw-1', keyword: 'window cleaning edmonton', searchVolume: 2900, intent: 'Commercial', priority: 'Primary', type: 'Target', source: 'Core Seed', targetPage: '/', action: 'Homepage Target', status: 'active' },
        { id: 'mk-rw-2', keyword: 'residential window cleaning', searchVolume: 1800, intent: 'Commercial', priority: 'Primary', type: 'Target', source: 'Core Seed', targetPage: '/residential-window-cleaning/', action: 'Residential Service Page', status: 'active' },
        { id: 'mk-rw-3', keyword: 'gutter cleaning edmonton', searchVolume: 1600, intent: 'Commercial', priority: 'Secondary', type: 'Target', source: 'Core Seed', targetPage: '/gutter-cleaning-edmonton/', action: 'Dedicated Gutter Page', status: 'active' },
        { id: 'mk-rw-4', keyword: 'commercial window cleaning edmonton', searchVolume: 1200, intent: 'Commercial', priority: 'Primary', type: 'Target', source: 'Seed Expansion', targetPage: '/commercial-window-cleaning/', action: 'Commercial B2B Service Page', status: 'active' },
      ],
      pageMappings: [
        { id: 'pm-rw-1', keyword: 'window cleaning edmonton', priority: 'Primary', intent: 'Commercial', targetPage: 'Homepage - Reflect Window Cleaning', existingOrNew: 'Existing Page', targetUrl: '/', reason: 'Core local revenue driver' },
        { id: 'pm-rw-2', keyword: 'residential window cleaning', priority: 'Primary', intent: 'Commercial', targetPage: 'Residential Window Cleaning Service Page', existingOrNew: 'Existing Page', targetUrl: '/residential-window-cleaning/', reason: 'High conversion residential hub' },
      ],
      contentBriefs: [],
      decisionLog: [],
      homepageSeoTitle: 'Professional Window Cleaning & Gutter Cleaning Edmonton | Reflect Window Cleaning',
      homepageMetaDescription: 'Reflect Window Cleaning provides streak-free residential & commercial window cleaning, gutter cleaning & pressure washing across Edmonton, AB. Get a free quote today!',
      actionPlanP1: [
        'Optimize local service silos for Residential Window Cleaning, Commercial Window Washing, and Gutter Cleaning',
        'Reinforce exact NAP consistency and Google Review badges across Edmonton service areas',
      ],
      actionPlanP2: [],
      actionPlanP3: [],
      actionPlanP4: [],
    };
  }

  static getProjectById(id: string): KeywordResearchProject | null {
    const list = this.getAllProjects();
    return list.find(p => p.id === id) || null;
  }

  static getActiveProjectId(): string | null {
    return localStorage.getItem(ACTIVE_KEYWORD_PROJECT_KEY);
  }

  static setActiveProjectId(id: string): void {
    localStorage.setItem(ACTIVE_KEYWORD_PROJECT_KEY, id);
  }

  static createProject(clientName: string, websiteUrl: string, primaryLocation: string = 'Miami, FL', linkedAuditId?: string): KeywordResearchProject {
    const projects = this.getAllProjects();
    const newProject: KeywordResearchProject = {
      id: `kw-project-${Date.now()}`,
      clientName,
      websiteUrl,
      linkedAuditId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      currentPhase: 0,
      businessProfile: {
        ...defaultBusinessProfile,
        businessName: clientName,
        websiteUrl,
        primaryLocation,
      },
      seedKeywords: [],
      variations: [],
      websiteContentKeywords: [],
      competitorKeywords: [],
      toolKeywords: [],
      autocompleteKeywords: [],
      aeoQuestions: [],
      masterKeywords: [],
      pageMappings: [],
      contentBriefs: [],
      decisionLog: [],
      actionPlanP1: [],
      actionPlanP2: [],
      actionPlanP3: [],
      actionPlanP4: [],
    };

    projects.unshift(newProject);
    localStorage.setItem(KEYWORD_PROJECTS_KEY, JSON.stringify(projects));
    this.setActiveProjectId(newProject.id);
    return newProject;
  }

  static saveProject(project: KeywordResearchProject): void {
    const projects = this.getAllProjects();
    const updatedProject = {
      ...project,
      updatedAt: new Date().toISOString(),
    };

    const idx = projects.findIndex(p => p.id === project.id);
    if (idx !== -1) {
      projects[idx] = updatedProject;
    } else {
      projects.unshift(updatedProject);
    }

    localStorage.setItem(KEYWORD_PROJECTS_KEY, JSON.stringify(projects));
    cloudSyncService.saveKeywordProjectToCloud(updatedProject).catch(() => {});
  }

  static deleteProject(id: string): void {
    const projects = this.getAllProjects().filter(p => p.id !== id);
    localStorage.setItem(KEYWORD_PROJECTS_KEY, JSON.stringify(projects));
    if (this.getActiveProjectId() === id) {
      localStorage.removeItem(ACTIVE_KEYWORD_PROJECT_KEY);
    }
  }
}
